import { useEffect, useState } from "react";
import {
  prepareCryptoControlBlock,
  LocalizationProvider,
} from "@rshbintech.dboul/utils";
import { AppLocale } from "../common/types";
import { translations } from "../translations";
import { COMPONENT_TYPES, SIGNING_TYPES } from "./constants";
import { SignedDocumentTypes, SigningDocumentProps } from "./types";
import { PositionContext } from "./position-context";
import { SigningWithSms } from "./signing-with-sms";
import { SigningWithCertificate } from "./signing-with-certificate";
import { RevokingSignature } from "./revoking-signature";

export const SigningDocument = ({
  componentType = "SIGNING",
  signingType = SIGNING_TYPES.SIGNING,
  buttonText,
  className,
  ccbVersion,
  documentId,
  signedDocument,
  documentFields,
  certificateId,
  certificateIssuer,
  signingWithSms = false,
  signingWithMonolith = false,
  onErrorSigning,
  onSuccessSigning,
  onClickSigningButton,
  onClickSendSmsPasswordButton,
  messageTemplate,
  position = "default",
  disableSignButton,
  revokeType,
  revokingClassName,
  documentType,
  onClickRevokingButton,
  language,
  dataAttributesRevokingSignatureButton,
  dataAttributesSigningWithCertificateButton,
  dataAttributesSigningWithSmsButton,
  dataAttributesSmsConfirmationButton,
  onSignatureChecked,
}: SigningDocumentProps) => {
  const [cryptoControlBlock, setCryptoControlBlock] = useState("");

  useEffect(() => {
    if (documentFields) {
      const cryptoControlBlock = prepareCryptoControlBlock(documentFields);

      setCryptoControlBlock(cryptoControlBlock);
    }

    return () => {
      setCryptoControlBlock("");
    };
  }, [documentFields, signingWithMonolith]);

  return (
    <PositionContext.Provider value={position}>
      <LocalizationProvider<AppLocale>
        language={language}
        fallBackLanguage="ru_RU"
        resources={translations}
        defaultNamespace="SIGNING_DOCUMENT"
      >
        {(componentType === COMPONENT_TYPES.SIGNING ||
          componentType === COMPONENT_TYPES.BOTH) && (
          <>
            {signingWithSms ? (
              <SigningWithSms
                disableSignButton={disableSignButton}
                buttonText={buttonText}
                className={className}
                documentId={documentId}
                onErrorSigning={onErrorSigning}
                onSuccessSigning={onSuccessSigning}
                cryptoControlBlock={cryptoControlBlock}
                signingWithMonolith={signingWithMonolith}
                onClickSigningButton={onClickSigningButton}
                onClickSendSmsPasswordButton={onClickSendSmsPasswordButton}
                messageTemplate={messageTemplate}
                ccbVersion={ccbVersion}
                signedDocument={
                  signedDocument as SignedDocumentTypes["confirmationDocumentType"]
                }
                dataAttributesSigningWithSmsButton={
                  dataAttributesSigningWithSmsButton
                }
                dataAttributesSmsConfirmationButton={
                  dataAttributesSmsConfirmationButton
                }
              />
            ) : (
              <SigningWithCertificate
                signingType={signingType}
                buttonText={buttonText}
                disableSignButton={disableSignButton}
                className={className}
                ccbVersion={ccbVersion}
                documentId={documentId}
                signedDocument={signedDocument}
                certificateId={certificateId}
                certificateIssuer={certificateIssuer}
                onErrorSigning={onErrorSigning}
                onSuccessSigning={onSuccessSigning}
                cryptoControlBlock={cryptoControlBlock}
                signingWithMonolith={signingWithMonolith}
                onClickSigningButton={onClickSigningButton}
                dataAttributesSigningWithCertificateButton={
                  dataAttributesSigningWithCertificateButton
                }
                onSignatureChecked={onSignatureChecked}
              />
            )}
          </>
        )}
        {(componentType === COMPONENT_TYPES.REVOKING ||
          componentType === COMPONENT_TYPES.BOTH) && (
          <RevokingSignature
            documentId={documentId!}
            buttonText={buttonText}
            documentType={documentType!}
            revokeType={revokeType!}
            className={revokingClassName}
            onErrorRevoking={onErrorSigning}
            onSuccessRevoking={onSuccessSigning}
            onClickRevokingButton={onClickRevokingButton}
            dataAttributesRevokingSignatureButton={
              dataAttributesRevokingSignatureButton
            }
          />
        )}
      </LocalizationProvider>
    </PositionContext.Provider>
  );
};
